import { createSlice } from "@reduxjs/toolkit";

/**
 * Preview Slice
 * Manages global preview modal state and cached data
 * Used to show preview from any screen without navigation
 */
const previewSlice = createSlice({
  name: "preview",
  initialState: {
    // Modal state
    isOpen: false,
    
    // Revision identifiers
    partId: null,
    revisionId: null,
    
    // Cached data (when available from parent screen)
    cachedRevisionData: null,
    cachedTrainingLogs: null,
    cachedBomTable: null,
    cachedCurrentRevision: null,
    cachedAllNodesData: null,
    
    // Draft/unsaved data (for previewing before save)
    draftSection: null,
    draftStep: null,
    draftSubstep: null,
    draftChild: null,
    
    // Draft BOM items to merge into main BOM table
    draftBomItems: null, // Array of BOM items from draft step
    
    // Draft revision data (for EditProcedureRevision unsaved changes)
    draftRevision: null, // { references, revision_details, etc. }
    
    // Control flags
    useCache: false, // If true, use cached data; if false, fetch fresh
    
    // Copy & Replace specific state
    copyReplaceMode: false,        // true when in copy & replace mode
    copyMode: 'document',          // 'document' | 'node' - type of copy operation
    sourceDocument: null,          // { partId, revisionId, partNumber, revision, type, description }
    sourceNode: null,              // For node copy: { nodeId, nodeType, nodeTitle, sectionId, stepId, substepId, ... }
    pastePosition: null,          // For node copy: { position: 'into' | 'above' | 'below', targetType, targetId, targetIndex }
    targetDocument: null,          // { partId, revisionId, partNumber, revision, type, description }
    originalData: null,            // Target document current state (allNodesData)
    newData: null,                 // Target document after copy (computed)
    previewMode: 'new',       // 'original' | 'new'
    comment: '',                   // User comment for copy operation
    isVersionHistory: false,       // true when copying from version history/audit trail
    auditTrailId: null,            // Audit trail ID for version history copy
    versionId: null,               // Version ID for version history copy
    isFromMovePreview: false,      // true when copying from MovePreviewLayout (skip source section validation)
    movePreviewSourceData: null,   // Source data from MovePreviewLayout (originingalContent) when isFromMovePreview is true
    isLoading: false,              // Loading state for copy operation
    
    // Delete specific state
    deleteMode: false,             // true when in delete mode
    deleteNode: null,              // { nodeId, nodeType, nodeTitle, sectionId, stepId, substepId, ... }
    deleteDocument: null,          // { partId, revisionId, partNumber, revision, type, description }
    deleteOriginalData: null,      // Current state before delete
    deleteNewData: null,           // State after delete (computed)
    
    // Move specific state
    moveMode: false,                // true when in move mode
    moveNode: null,                 // { nodeId, nodeType, nodeTitle, sectionId, stepId, substepId, ... }
    moveDocument: null,             // Source document (for same-doc move, this is the only document)
    moveTargetDocument: null,       // Target document (null if same document move)
    movePosition: null,             // { position: 'into' | 'above' | 'below', targetType, targetId, targetIndex }
    moveOriginalData: null,         // Current state before move
    moveNewData: null,              // State after move (computed)
    movePreviewMode: 'new',         // 'original' | 'new'
    moveComment: '',                // User comment for move operation
    moveIsLoading: false,           // Loading state for move operation
    moveCreateCopy: false,           // Create copy checkbox state
  },
  reducers: {
    /**
     * Open preview modal with fresh data fetch
     * Use this from Add/Edit screens where data needs to be refreshed
     * Can optionally include draft/unsaved data for preview
     */
    openPreview: (state, action) => {
      state.isOpen = true;
      state.partId = action.payload.partId;
      state.revisionId = action.payload.revisionId;
      state.useCache = false;
      // Clear cached data when opening without cache
      state.cachedRevisionData = null;
      state.cachedTrainingLogs = null;
      state.cachedBomTable = null;
      state.cachedCurrentRevision = null;
      state.cachedAllNodesData = null;
      // Set draft data if provided
      state.draftSection = action.payload.draftSection || null;
      state.draftStep = action.payload.draftStep || null;
      state.draftSubstep = action.payload.draftSubstep || null;
      state.draftChild = action.payload.draftChild || null;
      state.draftBomItems = action.payload.draftBomItems || null;
      state.draftRevision = action.payload.draftRevision || null;
    },
    
    /**
     * Open preview modal with cached data
     * Use this from main Document/Revision screen where data is already loaded
     */
    openPreviewWithCache: (state, action) => {
      state.isOpen = true;
      state.partId = action.payload.partId;
      state.revisionId = action.payload.revisionId;
      state.useCache = true;
      // Store cached data
      state.cachedRevisionData = action.payload.revisionData || null;
      state.cachedTrainingLogs = action.payload.trainingLogs || null;
      state.cachedBomTable = action.payload.bomTable || null;
      state.cachedCurrentRevision = action.payload.currentRevision || null;
      state.cachedAllNodesData = action.payload.allNodesData || null;
    },
    
    /**
     * Close preview modal
     * Keeps cached data but clears draft data
     */
    closePreview: (state) => {
      state.isOpen = false;
      // Clear draft data when closing
      state.draftSection = null;
      state.draftStep = null;
      state.draftSubstep = null;
      state.draftChild = null;
      state.draftBomItems = null;
      state.draftRevision = null;
    },
    
    /**
     * Clear all preview data
     * Use when navigating away from revision screens
     */
    clearPreviewData: (state) => {
      state.isOpen = false;
      state.partId = null;
      state.revisionId = null;
      state.cachedRevisionData = null;
      state.cachedTrainingLogs = null;
      state.cachedBomTable = null;
      state.cachedCurrentRevision = null;
      state.cachedAllNodesData = null;
      state.draftSection = null;
      state.draftStep = null;
      state.draftSubstep = null;
      state.draftChild = null;
      state.draftBomItems = null;
      state.draftRevision = null;
      state.useCache = false;
    },
    
    /**
     * Open copy & replace mode
     * Sets up source document and initializes copy & replace state
     */
    openCopyReplace: (state, action) => {
      
      state.isOpen = true;
      state.copyReplaceMode = true;
      state.copyMode = action.payload.copyMode || 'document'; // 'document' | 'node'
      state.sourceDocument = action.payload.sourceDocument;
      state.sourceNode = action.payload.sourceNode || null; // For node copy
      state.targetDocument = null;
      state.originalData = null;
      state.newData = null;
      state.previewMode = 'new';
      state.comment = '';
      state.isLoading = false;
      state.isVersionHistory = action.payload.isVersionHistory || false; // Flag for version history copy
      state.auditTrailId = action.payload.auditTrailId || null; // Audit trail ID for version history
      state.versionId = action.payload.versionId || null; // Version ID for version history
      state.isFromMovePreview = action.payload.isFromMovePreview || false; // Flag for MovePreviewLayout copy
      state.movePreviewSourceData = action.payload.movePreviewSourceData || null; // Source data from MovePreviewLayout
      state.breadcrumbText = action.payload.breadcrumbText || null; // Breadcrumb text for copy operation
      
    },
    
    /**
     * Set target document for copy & replace
     * Called when user selects target document from SelectRevisionTable
     */
    setTargetDocument: (state, action) => {
      state.targetDocument = action.payload;
    },
    
    /**
     * Set preview mode (original vs new)
     * Toggles between showing original target document vs new document after copy
     */
    setPreviewMode: (state, action) => {
      state.previewMode = action.payload;
    },
    
    /**
     * Set user comment for copy operation
     */
    setComment: (state, action) => {
      state.comment = action.payload;
    },
    
    /**
     * Set original data (target document current state)
     * Called when target document data is fetched
     */
    setOriginalData: (state, action) => {
      state.originalData = action.payload;
    },
    
    /**
     * Set new data (target document after copy)
     * Called when new document structure is computed
     */
    setNewData: (state, action) => {
      state.newData = action.payload;
    },
    
    /**
     * Set loading state for copy operation
     */
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    
    /**
     * Set paste position for node copy
     */
    setPastePosition: (state, action) => {
      state.pastePosition = action.payload;
    },
    
    /**
     * Close copy & replace mode
     * Clears all copy & replace specific state
     */
    closeCopyReplace: (state) => {
      state.isOpen = false;
      state.copyReplaceMode = false;
      state.copyMode = 'document';
      state.sourceDocument = null;
      state.sourceNode = null;
      state.pastePosition = null;
      state.targetDocument = null;
      state.originalData = null;
      state.newData = null;
      state.previewMode = 'new';
      state.comment = '';
      state.isLoading = false;
      state.isVersionHistory = false;
      state.isFromMovePreview = false;
      state.movePreviewSourceData = null;
      state.auditTrailId = null;
      state.versionId = null;
    },
    
    /**
     * Open delete mode
     * Sets up delete node and initializes delete state
     */
    openDelete: (state, action) => {
      state.isOpen = true;
      state.deleteMode = true;
      state.deleteDocument = action.payload.deleteDocument;
      state.deleteNode = action.payload.deleteNode;
      state.deleteOriginalData = null;
      state.deleteNewData = null;
      state.previewMode = 'new';
      state.comment = '';
      state.isLoading = false;
    },
    
    /**
     * Set original data for delete
     */
    setDeleteOriginalData: (state, action) => {
      state.deleteOriginalData = action.payload;
    },
    
    /**
     * Set new data after delete (computed)
     */
    setDeleteNewData: (state, action) => {
      state.deleteNewData = action.payload;
    },
    
    /**
     * Close delete mode
     * Clears all delete specific state
     */
    closeDelete: (state) => {
      state.isOpen = false;
      state.deleteMode = false;
      state.deleteNode = null;
      state.deleteDocument = null;
      state.deleteOriginalData = null;
      state.deleteNewData = null;
      state.previewMode = 'original';
      state.comment = '';
      state.isLoading = false;
    },
    
    /**
     * Open move mode
     * Sets up move node and initializes move state
     */
    openMove: (state, action) => {
      state.isOpen = true;
      state.moveMode = true;
      state.moveNode = action.payload.moveNode;
      state.moveDocument = action.payload.moveDocument;
      state.moveTargetDocument = action.payload.moveTargetDocument || null; // null for same-doc move
      state.movePosition = null;
      state.moveOriginalData = null;
      state.moveNewData = null;
      state.movePreviewMode = 'new';
      state.moveComment = '';
      state.moveIsLoading = false;
    },
    
    /**
     * Set target document for move (for cross-document moves)
     */
    setMoveTargetDocument: (state, action) => {
      state.moveTargetDocument = action.payload;
    },
    
    /**
     * Set move position
     */
    setMovePosition: (state, action) => {
      state.movePosition = action.payload;
    },
    
    /**
     * Set original data for move
     */
    setMoveOriginalData: (state, action) => {
      state.moveOriginalData = action.payload;
    },
    
    /**
     * Set new data after move (computed)
     */
    setMoveNewData: (state, action) => {
      state.moveNewData = action.payload;
    },
    
    /**
     * Set preview mode for move
     */
    setMovePreviewMode: (state, action) => {
      state.movePreviewMode = action.payload;
    },
    
    /**
     * Set comment for move operation
     */
    setMoveComment: (state, action) => {
      state.moveComment = action.payload;
    },
    
    /**
     * Set loading state for move operation
     */
    setMoveLoading: (state, action) => {
      state.moveIsLoading = action.payload;
    },
    
    /**
     * Set create copy checkbox state for move operation
     */
    setMoveCreateCopy: (state, action) => {
      state.moveCreateCopy = action.payload;
    },
    
    /**
     * Close move mode
     * Clears all move specific state
     */
    closeMove: (state) => {
      state.isOpen = false;
      state.moveMode = false;
      state.moveNode = null;
      state.moveDocument = null;
      state.moveTargetDocument = null;
      state.movePosition = null;
      state.moveOriginalData = null;
      state.moveNewData = null;
      state.movePreviewMode = 'original';
      state.moveComment = '';
      state.moveIsLoading = false;
      state.moveCreateCopy = false;
    },
  },
});

export const {
  openPreview,
  openPreviewWithCache,
  closePreview,
  clearPreviewData,
  openCopyReplace,
  setTargetDocument,
  setPreviewMode,
  setComment,
  setOriginalData,
  setNewData,
  setLoading,
  setPastePosition,
  closeCopyReplace,
  openDelete,
  setDeleteOriginalData,
  setDeleteNewData,
  closeDelete,
  openMove,
  setMoveTargetDocument,
  setMovePosition,
  setMoveOriginalData,
  setMoveNewData,
  setMovePreviewMode,
  setMoveComment,
  setMoveLoading,
  setMoveCreateCopy,
  closeMove,
} = previewSlice.actions;

export default previewSlice.reducer;

